import { format } from 'date-fns';
import { FC, useEffect, useState } from 'react';

import { useComboProgressContext } from '../../hooks/contexts/comboProgress.context';
import { useEventContext } from '../../hooks/contexts/event.context';
import { useI18nContext } from '../../hooks/contexts/i18n.context';
import useFormattedDateTimeZone from '../../hooks/useFormattedDateTimeZone';
import { ComboDate } from '../../types/ticket';
import ComboLayout from '../Layout/combo';
import Popover from '../Popover';
import * as Styles from './combo-session-list.styles';

type ComboDateTime = {
  date : string;
  weekAndHour : string;
} & ComboDate;

const ComboSessionsList: FC = () => {
  const [localSelectedSessions, setLocalSelectedSessions] = useState<string[]>([]);
  const { comboData } = useComboProgressContext();
  const { comboList, event } = useEventContext();
  const { formatDate } = useFormattedDateTimeZone();
  const {
    supportedLocales,
    translate,
    currentLocale : { locale : l },
  } = useI18nContext();

  const locale = supportedLocales({ lang : l });

  const selectedCombo = comboList.find((combo) => combo.id === comboData.id) || comboList[0];
  const formattedDateTimeCards: ComboDateTime[] = selectedCombo.comboDates.map((comboDate) => {
    const curDateTime = formatDate(comboDate.datetime, event?.timezone ?? 'America/Sao_Paulo');

    const hourFormat = locale.code === 'en-US' ? 'hh\':\'mm a' : 'HH\':\'mm';
    const hour = format(curDateTime, hourFormat, { locale }).toLowerCase();
    const week = format(curDateTime, 'eee', { locale }).substring(0, 3);
    const day = format(curDateTime, 'dd', { locale });
    const month = format(curDateTime, 'MMMM', { locale }).substring(0, 3);
    const year = format(curDateTime, 'yyyy', { locale });

    const expectedDate = locale.code === 'en-US' ? (
      `${month} ${day} ${year}`
    ) : `${day} ${month} ${year}`;

    const expectedWeekAndHour = `${week} • ${hour}`;

    return {
      date : expectedDate,
      weekAndHour : expectedWeekAndHour,
      ...comboDate,
    };
  });

  const handleSelectSessions = (comboDateTime: ComboDateTime) => {
    if (comboDateTime.required) return;
    const alreadySelected = localSelectedSessions.includes(String(comboDateTime.id));
    if (alreadySelected)
      setLocalSelectedSessions((prev) => prev.filter((id) => id !== String(comboDateTime.id)));
    else
      setLocalSelectedSessions((prev) => [...prev, String(comboDateTime.id)]);
  };

  const handleSelectedSessions = (comboDateTime: ComboDateTime) => (
    comboDateTime.required || localSelectedSessions.includes(String(comboDateTime.id))
  );

  const btnIsDisable = () => {
    const ltMin = localSelectedSessions.length < selectedCombo.sessionMin;
    const gtMax = localSelectedSessions.length > selectedCombo.sessionMax;

    return ltMin || gtMax;
  };

  useEffect(() => {
    const requiredSessions = formattedDateTimeCards.filter((comboDateTime) => comboDateTime.required)
      .map((comboDateTime) => String(comboDateTime.id));

    setLocalSelectedSessions(requiredSessions);
  }, []);

  return (
    <ComboLayout
      curComponent="sessions"
      btnIsDisable={btnIsDisable()}
      selectedSession={localSelectedSessions}
    >
      <Styles.ComboListContainer>
        <Styles.ComboListTitle>
          {translate(['COMBO', 'COMBO_SESSION_LIST', 'TITLE'])}
        </Styles.ComboListTitle>
        <Styles.ListOfCombos>
          {formattedDateTimeCards.map((curDateTime: ComboDateTime) => (
            <Styles.ComboListItem
              key={curDateTime.id}
              $selected={handleSelectedSessions(curDateTime)}
            >
              <label
                htmlFor={String(curDateTime.id)}
              >
                {curDateTime.required ? (
                  <Popover
                    id={`combo-tooltip-${curDateTime.id}`}
                    text={translate(['COMBO', 'COMBO_SESSION_LIST', 'POPOVER'])}
                  >
                    <strong>✔</strong>
                  </Popover>
                ) : (
                  <input
                    onClick={() => handleSelectSessions(curDateTime)}
                    type="checkbox"
                    id={String(curDateTime.id)}
                  />
                )}
                <div>
                  <span>{curDateTime.date}</span>
                  <small>{curDateTime.weekAndHour}</small>
                </div>
              </label>
            </Styles.ComboListItem>
          ))}
        </Styles.ListOfCombos>
      </Styles.ComboListContainer>
    </ComboLayout>
  );
};

export default ComboSessionsList;
